import React, { useMemo, useState } from 'react';
import { getAllMockPitchers } from '../utils/mockPlayerNames';
import { PITCHERS_SEASON_AGG } from '../data/pitchersSeasonAggregates';
import { MOCK_TO_REAL_PITCHER_MAPPING } from '../utils/mockPlayerNames';
import CustomDropdown from '../components/ui/CustomDropdown';
// Charts now use custom HTML/CSS implementation for better reliability

// Professional Tooltip Component
const PitchTooltip = ({ pitch, whiffRate, hardContactRate, visible, x, y }) => {
  if (!visible) return null;
  
  return (
    <div 
      className="absolute z-[9999] bg-slate-800/95 backdrop-blur-sm border border-white/20 rounded-lg p-3 shadow-2xl pointer-events-none"
      style={{ 
        left: x + 10, 
        top: y - 10,
        transform: 'translateY(-100%)'
      }}
    >
      <div className="text-white font-semibold text-sm mb-1">{pitch}</div>
      <div className="space-y-1 text-xs">
        <div className="text-white/80">
          <span className="text-white/60">Whiff Rate:</span> <span className="font-semibold text-white">{whiffRate}%</span>
        </div>
        <div className="text-white/80">
          <span className="text-white/60">Hard Contact:</span> <span className="font-semibold text-white">{hardContactRate}%</span>
        </div>
      </div>
    </div>
  );
};

/* ------------------------ Thunder UI helpers ------------------------ */
const Card = ({ children, className = "" }) => (
  <div className={`rounded-2xl border border-white/10 bg-slate-800 text-white shadow-xl ${className}`}>
    {children}
  </div>
);

const CardContent = ({ children, className = "" }) => {
  const hasCustomPadding = className.includes('p-');
  const paddingClass = hasCustomPadding ? '' : 'p-6';
  return <div className={`${paddingClass} ${className}`}>{children}</div>;
};

/* ------------------------ Pitch selector ------------------------ */
const PitchSelector = ({ selectedPitch, onPitchSelect, pitchData }) => {
  const pitchColors = {
    'Four-Seam Fastball': { bg: 'bg-blue-500', text: 'text-blue-400', border: 'border-blue-400' },
    'Slider': { bg: 'bg-yellow-500', text: 'text-yellow-400', border: 'border-yellow-400' },
    'Changeup': { bg: 'bg-green-500', text: 'text-green-400', border: 'border-green-400' },
    'Sinker': { bg: 'bg-purple-500', text: 'text-purple-400', border: 'border-purple-400' },
    'Curveball': { bg: 'bg-red-500', text: 'text-red-400', border: 'border-red-400' }
  };

  return (
    <div className="space-y-3">
      {Object.entries(pitchData).map(([pitchType, data]) => {
        const colors = pitchColors[pitchType] || { bg: 'bg-gray-500', text: 'text-gray-400', border: 'border-gray-400' };
        const isSelected = selectedPitch === pitchType;

        return (
          <div
            key={pitchType}
            onClick={() => onPitchSelect(pitchType)}
            className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
              isSelected ? `${colors.border} bg-slate-700` : 'border-slate-600 bg-slate-800 hover:bg-slate-700'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${colors.bg}`} />
                <div className="font-semibold">{pitchType.replace('Four-Seam ', '')}</div>
              </div>
              <div className={`text-lg font-bold ${colors.text}`}>{data.usage}%</div>
            </div>

            {isSelected && (
              <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-white/60">Velocity:</span><span className="ml-2 font-semibold">{data.velocity} mph</span></div>
                <div><span className="text-white/60">Whiff%:</span><span className="ml-2 font-semibold">{data.whiffRate}%</span></div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

/* ------------------------ Page ------------------------ */
export default function PitchTypeAnalysisPage() {
  const [selectedPitcher, setSelectedPitcher] = useState('Carlos Alvarez');
  const [selectedPitch, setSelectedPitch] = useState('Four-Seam Fastball');
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, pitch: '', whiffRate: 0, hardContactRate: 0 });
  const mockPitchers = getAllMockPitchers();

  /* ---- build pitchData (real mix + movement, mock whiff/extension for now) ---- */
  const pitchData = useMemo(() => {
    if (!selectedPitcher) return {};

    const realPitcherName = MOCK_TO_REAL_PITCHER_MAPPING[selectedPitcher];
    if (!realPitcherName) {
      console.warn(`No real pitcher mapping found for: ${selectedPitcher}`);
      return {};
    }

    const pitcherData = PITCHERS_SEASON_AGG.find(p => p.name === realPitcherName);
    if (!pitcherData || !pitcherData.pitches) {
      console.warn(`No pitch data found for: ${realPitcherName}`);
      return {};
    }

    const totalPitches = Object.values(pitcherData.pitches).reduce((sum, pitch) => sum + pitch.n, 0);
    const map = {
      fourSeam: 'Four-Seam Fastball',
      slider: 'Slider',
      changeup: 'Changeup',
      sinker: 'Sinker',
      curveball: 'Curveball',
      cutter: 'Cutter',
      sweeper: 'Sweeper',
    };

    const out = {};
    console.log(`RAW PITCHER DATA for ${selectedPitcher}:`, pitcherData.pitches);
    console.log(`Total pitches: ${totalPitches}`);
    
    Object.entries(pitcherData.pitches).forEach(([k, d]) => {
      console.log(`Processing pitch type: ${k}, count: ${d.n}`);
      if (k === 'cutter' || k === 'sweeper') {
        console.log(`Skipping ${k} (filtered out)`);
        return; // show main 5
      }
      const display = map[k] || k;
      const usage = totalPitches ? (d.n / totalPitches) * 100 : 0;
      console.log(`${k} -> ${display}, usage: ${usage}%`);

      out[display] = {
        usage: Math.round(usage * 10) / 10,
        velocity: Math.round(d.p50Velo * 10) / 10,
        spin: Math.round((d.p50Spin || 2200) * 10) / 10,
        whiffRate: Math.round((10 + Math.random() * 15) * 10) / 10, // mock for now
        ivb: Math.round(d.p50IVB * 10) / 10,
        hb: Math.round(d.p50HB * 10) / 10,
        extension: Math.round((6 + Math.random() * 0.8) * 10) / 10,
        count: d.n,
      };
      console.log(`Added to out:`, out[display]);
    });

    console.log(`Loaded real data for ${selectedPitcher} (${realPitcherName}):`, out);
    return out;
  }, [selectedPitcher]);

  /* ---- helpers ---- */
  const labelShort = (name) => {
    const shortNames = {
      'Four-Seam Fastball': 'FB',
      'Slider': 'SL', 
      'Changeup': 'CH',
      'Sinker': 'SI',
      'Curveball': 'CB'
    };
    return shortNames[name] || name;
  };

  /* ---- stable chart datasets (avoid inline map in JSX) ---- */
  const commandMetricsData = useMemo(() => {
    if (!pitchData || Object.keys(pitchData).length === 0) {
      console.log('Command Metrics Data: No pitch data available');
      return [];
    }
    const arr = Object.entries(pitchData).map(([pitchType, d]) => ({
      pitch: labelShort(pitchType),
      strikeRate: Math.round(d.usage || 0), // proxy for now
      zoneRate: Math.round(50 + Math.random() * 20),
      firstStrikeRate: Math.round(55 + Math.random() * 20),
    }));
    console.log('Command Metrics Data:', arr);
    return arr;
  }, [pitchData]);

  const swingMissData = useMemo(() => {
    // Get realistic pitcher-specific data based on scouting reports
    const getPitcherProfile = (pitcher) => {
      const profiles = {
        // ELITE TIER (FV 65+) - YOUR 3 FEATURED PITCHERS
        'Carlos Alvarez': { // Jude Abbadessa - ELITE TIER - Featured Thunder pitcher
          FB: { whiff: 42, hardContact: 16 }, // ELITE fastball - swing & miss pitch
          SL: { whiff: 46, hardContact: 14 }, // ELITE slider - swing & miss pitch
          CH: { whiff: 28, hardContact: 38 }, // Hit often but controlled
          SI: { whiff: 19, hardContact: 44 }, // Hit hard - weaker pitch
          CB: { whiff: 35, hardContact: 26 }  // Solid secondary
        },
        'Miguel Acosta': { // Miguel Sime - ELITE TIER - Featured Thunder pitcher
          FB: { whiff: 38, hardContact: 18 }, // Elite velocity (97.8 mph)
          SL: { whiff: 44, hardContact: 15 }, // ELITE slider - swing & miss pitch
          CH: { whiff: 41, hardContact: 17 }, // ELITE changeup - swing & miss pitch
          SI: { whiff: 22, hardContact: 36 }, // Hit often but controlled
          CB: { whiff: 18, hardContact: 42 }  // Hit hard - weaker pitch
        },
        'Trevor Evans': { // FV 55 - GOOD TIER - Above average pitcher
          FB: { whiff: 32, hardContact: 22 }, // Plus FB with carry - swing & miss pitch
          SL: { whiff: 38, hardContact: 19 }, // Plus slider - swing & miss pitch
          CH: { whiff: 24, hardContact: 34 }, // Hit often but controlled
          SI: { whiff: 18, hardContact: 40 }, // Hit hard - weaker pitch
          CB: { whiff: 28, hardContact: 28 }  // Solid secondary
        },
        
        'Alex Blair': { // Bryson Dudley - ELITE TIER (FV 65+) - Featured Thunder pitcher
          FB: { whiff: 36, hardContact: 20 }, // Elite FB with plus velocity and command
          SL: { whiff: 48, hardContact: 13 }, // ELITE slider - swing & miss pitch
          CH: { whiff: 43, hardContact: 16 }, // ELITE changeup - swing & miss pitch  
          SI: { whiff: 24, hardContact: 37 }, // Hit often but controlled
          CB: { whiff: 16, hardContact: 45 }  // Hit hard - weaker pitch
        },
        
        // GOOD TIER (FV 50-55)
        'Connor Mason': { // FV 50, good velocity - GOOD TIER
          FB: { whiff: 28, hardContact: 24 }, // Good velocity FB - swing & miss pitch
          SL: { whiff: 34, hardContact: 22 }, // Sharp bite SL - swing & miss pitch
          CH: { whiff: 20, hardContact: 38 }, // Hit often but controlled
          SI: { whiff: 16, hardContact: 42 }, // Hit hard - weaker pitch
          CB: { whiff: 26, hardContact: 30 }  // Solid secondary
        },
        'Raymond Ortiz': { // FV 50, good FB/CH
          FB: { whiff: 25, hardContact: 25 }, // FB with ride
          SL: { whiff: 22, hardContact: 35 }, // Needs work SL
          CH: { whiff: 35, hardContact: 20 }, // Advanced CH feel
          SI: { whiff: 15, hardContact: 40 }, // No sinker
          CB: { whiff: 18, hardContact: 42 }  // Developing CB
        },
        
        // AVERAGE TIER (FV 45) - 1 swing & miss pitch, 2 weak contact, 2 hit often/hard
        'Jake Ellis': { // FV 45, org depth
          FB: { whiff: 18, hardContact: 38 }, // Hit often - main weakness
          SL: { whiff: 32, hardContact: 18 }, // SWING & MISS - best pitch
          CH: { whiff: 20, hardContact: 25 }, // Weak contact - solid secondary
          SI: { whiff: 16, hardContact: 28 }, // Weak contact - decent pitch
          CB: { whiff: 12, hardContact: 45 }  // Hit hard - worst pitch
        },
        'Miles Newman': { // FV 45, org depth
          FB: { whiff: 28, hardContact: 22 }, // SWING & MISS - best pitch (good velocity)
          SL: { whiff: 18, hardContact: 40 }, // Hit often - developing
          CH: { whiff: 22, hardContact: 26 }, // Weak contact - arm speed helps
          SI: { whiff: 14, hardContact: 30 }, // Weak contact - decent feel
          CB: { whiff: 8, hardContact: 48 }   // Hit hard - worst pitch
        },
        
        // BELOW AVERAGE TIER (FV 40) - Maybe 1 swing & miss, 1 weak contact, 3 hit often/hard
        'Chris Clark': { // FV 40, middle relief
          FB: { whiff: 15, hardContact: 42 }, // Hit often - struggles
          SL: { whiff: 26, hardContact: 24 }, // SWING & MISS - only good pitch
          CH: { whiff: 12, hardContact: 45 }, // Hit hard - poor pitch
          SI: { whiff: 18, hardContact: 30 }, // Weak contact - sinker helps
          CB: { whiff: 8, hardContact: 50 }   // Hit hard - worst pitch
        },
        'Preston Lewis': { // FV 40, org depth
          FB: { whiff: 12, hardContact: 44 }, // Hit often - little life
          SL: { whiff: 18, hardContact: 42 }, // Hit often - lacks bite
          CH: { whiff: 24, hardContact: 28 }, // Weak contact - best pitch (change speeds)
          SI: { whiff: 8, hardContact: 50 },  // Hit hard - no sinker
          CB: { whiff: 15, hardContact: 48 }  // Hit hard - developing
        },
        
        // POOR TIER (FV 35 and below) - Even bad pitchers have SOME variety!
        'David Bennett': { // FV 35, org depth - LHP has changeup advantage
          FB: { whiff: 8, hardContact: 48 }, // Hit hard - limited life
          SL: { whiff: 12, hardContact: 25 }, // Weak contact - lefty slider has some deception
          CH: { whiff: 28, hardContact: 18 }, // SWING & MISS - LHP changeup is legit weapon
          SI: { whiff: 6, hardContact: 42 },  // Hit often - no sinker but changes speeds
          CB: { whiff: 8, hardContact: 50 }   // Hit hard - below-average
        },
        'Ryan Dean': { // FV 30, development project
          FB: { whiff: 6, hardContact: 52 }, // Hit hard - lacks carry
          SL: { whiff: 24, hardContact: 28 }, // Weak contact - best pitch, shows some bite
          CH: { whiff: 12, hardContact: 40 }, // Hit often - shows fade but inconsistent
          SI: { whiff: 4, hardContact: 54 },  // Hit hard - no sinker
          CB: { whiff: 18, hardContact: 35 }  // Weak contact - slow curve can fool hitters
        },
        'Josh Doyle': { // FV 25, non-prospect
          FB: { whiff: 4, hardContact: 54 }, // Hit hard - light FB
          SL: { whiff: 8, hardContact: 50 }, // Hit hard - lacks bite
          CH: { whiff: 22, hardContact: 30 }, // Weak contact - only pitch with deception
          SI: { whiff: 3, hardContact: 56 },  // Hit hard - no sinker
          CB: { whiff: 14, hardContact: 38 }  // Hit often - slow curve changes eye level
        },
        'Blake Kane': { // FV 35, deep development
          FB: { whiff: 7, hardContact: 50 }, // Hit hard - lacks carry
          SL: { whiff: 26, hardContact: 22 }, // Weak contact - best pitch, shows some shape
          CH: { whiff: 8, hardContact: 48 }, // Hit hard - limited feel
          SI: { whiff: 5, hardContact: 52 },  // Hit hard - no sinker
          CB: { whiff: 16, hardContact: 38 }  // Hit often - developing curve
        },
        
        // ADDITIONAL PITCHERS FROM SCOUTING REPORTS
        'Codi Knight': { // FV 35, development project
          FB: { whiff: 8, hardContact: 48 }, // Hit hard - flat FB
          SL: { whiff: 30, hardContact: 20 }, // SWING & MISS - inconsistent but flashes plus shape
          CH: { whiff: 14, hardContact: 26 }, // Weak contact - flashes separation
          SI: { whiff: 6, hardContact: 52 },  // Hit hard - no sinker
          CB: { whiff: 18, hardContact: 38 }  // Hit often - inconsistent but changes speeds
        },
        'Jackson Long': { // FV 35, org filler
          FB: { whiff: 6, hardContact: 52 }, // Hit hard - plays flat
          SL: { whiff: 8, hardContact: 48 }, // Hit hard - lacks bite
          CH: { whiff: 24, hardContact: 28 }, // Weak contact - lacks separation but changes speeds
          SI: { whiff: 4, hardContact: 54 },  // Hit hard - no sinker
          CB: { whiff: 20, hardContact: 22 }  // Weak contact - slow curve is decent pitch
        },
        'Drew Marsh': { // FV 35, conversion candidate
          FB: { whiff: 5, hardContact: 54 }, // Hit hard - light FB
          SL: { whiff: 32, hardContact: 18 }, // SWING & MISS - slider shows bite (best pitch)
          CH: { whiff: 8, hardContact: 50 }, // Hit hard - lacks finish
          SI: { whiff: 3, hardContact: 56 },  // Hit hard - no sinker
          CB: { whiff: 9, hardContact: 48 }   // Lacks finish
        },
        'Kyle Nicholson': { // FV 50, starter/power relief
          FB: { whiff: 28, hardContact: 22 }, // FB with late ride, misses bats up
          SL: { whiff: 38, hardContact: 18 }, // Tight tilt, projects above-average
          CH: { whiff: 22, hardContact: 32 }, // Usable vs LHH
          SI: { whiff: 14, hardContact: 40 }, // No sinker
          CB: { whiff: 18, hardContact: 38 }  // Mix-in CB
        },
        'Caden Pierce': { // FV 50, dependable starter
          FB: { whiff: 18, hardContact: 32 }, // FB 89-92
          SL: { whiff: 35, hardContact: 20 }, // Primary bat-misser with tight break
          CH: { whiff: 24, hardContact: 30 }, // Average projection
          SI: { whiff: 12, hardContact: 42 }, // No sinker
          CB: { whiff: 26, hardContact: 28 }  // Shows depth
        },
        'Jayden Parker': { // FV 45, org depth/swingman
          FB: { whiff: 14, hardContact: 36 }, // Plays best east-west
          SL: { whiff: 26, hardContact: 28 }, // Average projection with late tilt
          CH: { whiff: 16, hardContact: 40 }, // Lags behind
          SI: { whiff: 10, hardContact: 44 }, // No sinker
          CB: { whiff: 20, hardContact: 35 }  // Loops for strikes
        },
        'Kevin Ray': { // FV 45, relief/swingman
          FB: { whiff: 22, hardContact: 30 }, // Can play in-zone when elevated
          SL: { whiff: 24, hardContact: 32 }, // Flashes average with cutter shape
          CH: { whiff: 12, hardContact: 44 }, // Strike rate volatile
          SI: { whiff: 8, hardContact: 48 },  // No sinker
          CB: { whiff: 14, hardContact: 42 }  // Will need simpler lanes
        },
        'Preston Reed': { // FV 45, org starter
          FB: { whiff: 16, hardContact: 34 }, // Plays plane-down when downhill
          SL: { whiff: 24, hardContact: 30 }, // Flash average
          CH: { whiff: 26, hardContact: 28 }, // Flash average
          SI: { whiff: 10, hardContact: 45 }, // No sinker
          CB: { whiff: 12, hardContact: 42 }  // Show-me pitch
        },
        'Paulo Salazar': { // FV 40, org depth
          FB: { whiff: 12, hardContact: 40 }, // Steady FB
          SL: { whiff: 16, hardContact: 38 }, // Shows but lacks finish
          CH: { whiff: 18, hardContact: 36 }, // Shows but lacks finish
          SI: { whiff: 8, hardContact: 46 },  // No sinker
          CB: { whiff: 14, hardContact: 40 }  // Strike-throwing serviceable
        },
        'Xavier Simmons': { // FV 40, org filler
          FB: { whiff: 10, hardContact: 44 }, // Plays fringy but usable
          SL: { whiff: 14, hardContact: 42 }, // Flashes but lacks consistency
          CH: { whiff: 16, hardContact: 38 }, // Developing
          SI: { whiff: 6, hardContact: 50 },  // No sinker
          CB: { whiff: 8, hardContact: 48 }   // Minimal bite
        },
        'Cullen Thomas': { // FV 45, org starter
          FB: { whiff: 16, hardContact: 36 }, // FB with arm-side run
          SL: { whiff: 22, hardContact: 32 }, // Fringe-average trending to average
          CH: { whiff: 24, hardContact: 30 }, // Fringe-average trending to average
          SI: { whiff: 10, hardContact: 44 }, // No sinker
          CB: { whiff: 18, hardContact: 38 }  // Works as mix-in
        },
        'Miles Tracy': { // FV 40, developmental prospect
          FB: { whiff: 12, hardContact: 42 }, // Projects to 90 with mass
          SL: { whiff: 18, hardContact: 36 }, // Shows occasional sweep
          CH: { whiff: 14, hardContact: 40 }, // Developing
          SI: { whiff: 8, hardContact: 48 },  // No sinker
          CB: { whiff: 10, hardContact: 46 }  // Command is swing skill
        },
        'Vincent White': { // FV 45, org depth/swingman
          FB: { whiff: 18, hardContact: 34 }, // Plays well when commanded
          SL: { whiff: 22, hardContact: 32 }, // Flashes depth, could be average
          CH: { whiff: 24, hardContact: 30 }, // Shows fade, chance to miss bats
          SI: { whiff: 12, hardContact: 42 }, // No sinker
          CB: { whiff: 16, hardContact: 38 }  // Workable but lags
        },
        'Peter Winters': { // FV 40, development arm
          FB: { whiff: 14, hardContact: 38 }, // Good life, could add velocity
          SL: { whiff: 26, hardContact: 26 }, // Most advanced pitch with sweep/bite
          CH: { whiff: 16, hardContact: 40 }, // Playable but behind
          SI: { whiff: 10, hardContact: 44 }, // No sinker
          CB: { whiff: 18, hardContact: 36 }  // Playable but behind
        },
        'Leo West': { // FV 35, org arm
          FB: { whiff: 8, hardContact: 48 }, // Lacks life
          SL: { whiff: 12, hardContact: 44 }, // Loose breaking pitches
          CH: { whiff: 10, hardContact: 46 }, // Flashes fade but below-average
          SI: { whiff: 6, hardContact: 52 },  // No sinker
          CB: { whiff: 8, hardContact: 48 }   // Loose breaking pitches
        },
        'Jason Young': { // FV 40, org depth
          FB: { whiff: 14, hardContact: 38 }, // Average with arm-side run
          SL: { whiff: 16, hardContact: 40 }, // Playable but lacks impact
          CH: { whiff: 22, hardContact: 32 }, // Best secondary with tumble/fade
          SI: { whiff: 18, hardContact: 34 }, // Natural arm-side run (treat as sinker)
          CB: { whiff: 14, hardContact: 42 }  // Playable but lacks impact
        },
        'Luke Zimmerman': { // FV 40, development project
          FB: { whiff: 12, hardContact: 42 }, // Modest carry, could tick up
          SL: { whiff: 24, hardContact: 30 }, // Shows bite, projects average
          CH: { whiff: 14, hardContact: 40 }, // Lags behind
          SI: { whiff: 8, hardContact: 46 },  // No sinker
          CB: { whiff: 10, hardContact: 44 }  // Lags behind
        }
      };
      
      // Default profile for unlisted pitchers (average/below-average)
      const defaultProfile = {
        FB: { whiff: 12, hardContact: 40 },
        SL: { whiff: 20, hardContact: 35 },
        CH: { whiff: 16, hardContact: 38 },
        SI: { whiff: 10, hardContact: 42 },
        CB: { whiff: 18, hardContact: 36 }
      };
      
      const foundProfile = profiles[pitcher];
      if (!foundProfile) {
        console.error(`❌ NO PROFILE FOUND FOR ${pitcher}! Available profiles:`, Object.keys(profiles));
        console.log(`Using default profile for ${pitcher}`);
      }
      return foundProfile || defaultProfile;
    };
    
    const profile = getPitcherProfile(selectedPitcher);
    console.log(`🎯 SELECTED PITCHER: ${selectedPitcher}`);
    console.log(`📊 PROFILE FOR ${selectedPitcher}:`, profile);
    
    // Log each pitch's data for debugging
    Object.entries(profile).forEach(([pitch, data]) => {
      console.log(`  ${pitch}: ${data.whiff}% whiff, ${data.hardContact}% hard contact`);
    });
    
    // Only use pitches that exist in the actual data
    const arr = Object.entries(pitchData).map(([pitchType, d]) => {
      const pitchAbbrev = labelShort(pitchType);
      const pitchProfile = profile[pitchAbbrev];
      
      if (!pitchProfile) {
        console.warn(`No profile found for pitch ${pitchAbbrev}, using defaults`);
        return {
          pitch: pitchAbbrev,
          whiffRate: 15,
          hardContactRate: 35,
        };
      }
      
      return {
        pitch: pitchAbbrev,
        whiffRate: pitchProfile.whiff,
        hardContactRate: pitchProfile.hardContact,
      };
    });
    
    console.log(`Realistic Swing & Miss Data for ${selectedPitcher}:`, arr);
    return arr;
  }, [pitchData, selectedPitcher]);

  const platoonEffectivenessData = useMemo(() => {
    const arr = Object.entries(pitchData).map(([pitchType, d]) => ({
      pitch: labelShort(pitchType),
      lhbEffectiveness: Math.round(70 + Math.random() * 20),
      rhbEffectiveness: Math.round(65 + Math.random() * 20),
      usage: Math.round(d.usage || 0),
    }));
    console.log('Platoon Effectiveness Data:', arr);
    return arr;
  }, [pitchData]);

  /* ------------------------ render ------------------------ */
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-blue-900 to-slate-900 text-white">
      {/* HEADER */}
      <div className="border-b border-white/10">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3">
          <h1 className="text-2xl font-bold tracking-tight text-white">⚡ Pitch Type Analysis</h1>
          <div className="text-sm text-white/70">Advanced pitch-by-pitch breakdown and movement analysis</div>
        </div>
      </div>

      <main className="mx-auto max-w-7xl px-4 pb-4">
        {selectedPitcher ? (
          <>
            {/* ULTRA-COMPACT HEADER */}
            <div className="mt-2 flex items-center justify-between bg-slate-800/50 rounded-lg px-3 py-2 border border-white/10">
              <div className="flex items-center gap-3">
                <span className="text-white/70 text-xs">Pitcher:</span>
                <CustomDropdown
                  value={selectedPitcher}
                  onChange={(e) => setSelectedPitcher(e.target.value)}
                  options={mockPitchers.map(p => ({ value: p, label: p }))}
                  className="w-40"
                  placeholder="Select Pitcher"
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-white/70 text-sm">Mix:</span>
                {Object.entries(pitchData).map(([pitchType, data]) => {
                  const colors = {
                    'Four-Seam Fastball': '#3B82F6',
                    'Slider': '#F59E0B', 
                    'Changeup': '#10B981',
                    'Sinker': '#8B5CF6',
                    'Curveball': '#EF4444'
                  };
                  const color = colors[pitchType] || '#9CA3AF';
                  const shortName = pitchType.replace('Four-Seam ', '').replace('Fastball', 'FB');
                  
                  return (
                    <div key={pitchType} className="flex items-center gap-1">
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
                      <span className="text-white text-sm font-medium">{shortName}</span>
                      <span className="text-white/80 text-sm font-bold">{data.usage}%</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* CONDENSED 2-COLUMN LAYOUT */}
            <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-4">
              
              {/* LEFT COLUMN - MOVEMENT & COMMAND */}
              <div className="space-y-4">
                {/* MOVEMENT FINGERPRINT - READABLE */}
                <Card className="min-w-0">
                  <CardContent className="p-4">
                    <div className="mb-3">
                      <h3 className="text-xl font-bold text-white mb-1">Movement Map</h3>
                      <p className="text-sm text-white/70">Pitch movement profiles • Hover dots for velocity and break data</p>
                    </div>
                    <div className="h-96 bg-slate-700/30 rounded-lg p-4 min-w-0">
                      <svg width="100%" height="100%" viewBox="0 0 800 420" className="rounded-lg">
                        <defs>
                          <pattern id="movementGrid" width="60" height="40" patternUnits="userSpaceOnUse">
                            <path d="M 60 0 L 0 0 0 40" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
                          </pattern>
                        </defs>
                        <rect width="100%" height="100%" fill="url(#movementGrid)" />
                        
                        {/* Professional grid lines - CROPPED TO DATA RANGE */}
                        <line x1="200" y1="60" x2="200" y2="360" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
                        <line x1="280" y1="60" x2="280" y2="360" stroke="rgba(255,255,255,0.25)" strokeWidth="2" />
                        <line x1="360" y1="60" x2="360" y2="360" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
                        <line x1="440" y1="60" x2="440" y2="360" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
                        
                        <line x1="120" y1="340" x2="680" y2="340" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
                        <line x1="120" y1="260" x2="680" y2="260" stroke="rgba(255,255,255,0.25)" strokeWidth="2" />
                        <line x1="120" y1="180" x2="680" y2="180" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
                        <line x1="120" y1="100" x2="680" y2="100" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
                        
                        {/* Main axes - enhanced */}
                        <line x1="120" y1="360" x2="680" y2="360" stroke="#ffffff" strokeWidth="3" opacity="0.9" />
                        <line x1="120" y1="60" x2="120" y2="360" stroke="#ffffff" strokeWidth="3" opacity="0.9" />
                        <text x="400" y="410" textAnchor="middle" fill="#ffffff" fontSize="18" fontWeight="600">Horizontal Break (in)</text>
                        <text x="40" y="210" textAnchor="middle" fill="#ffffff" fontSize="18" fontWeight="600" transform="rotate(-90, 40, 210)">Induced Vertical Break (in)</text>

                        {/* X-axis labels - STRAIGHT LINE & SOLID WHITE */}
                        <g stroke="#ffffff" strokeWidth="1" fill="#ffffff" fontSize="16" fontWeight="600">
                          <line x1="200" y1="360" x2="200" y2="368" /><text x="200" y="385" textAnchor="middle" opacity="1.0">-10</text>
                          <line x1="240" y1="360" x2="240" y2="368" /><text x="240" y="385" textAnchor="middle" opacity="1.0">-5</text>
                          <line x1="280" y1="360" x2="280" y2="368" /><text x="280" y="385" textAnchor="middle" opacity="1.0">0</text>
                          <line x1="320" y1="360" x2="320" y2="368" /><text x="320" y="385" textAnchor="middle" opacity="1.0">5</text>
                          <line x1="360" y1="360" x2="360" y2="368" /><text x="360" y="385" textAnchor="middle" opacity="1.0">10</text>
                          <line x1="400" y1="360" x2="400" y2="368" /><text x="400" y="385" textAnchor="middle" opacity="1.0">15</text>
                          <line x1="440" y1="360" x2="440" y2="368" /><text x="440" y="385" textAnchor="middle" opacity="1.0">20</text>
                        </g>

                        {/* Y-axis labels - STRAIGHT LINE & SOLID WHITE */}
                        <g stroke="#ffffff" strokeWidth="1" fill="#ffffff" fontSize="16" fontWeight="600">
                          <line x1="112" y1="340" x2="120" y2="340" /><text x="105" y="346" textAnchor="end" opacity="1.0">-10</text>
                          <line x1="112" y1="300" x2="120" y2="300" /><text x="105" y="306" textAnchor="end" opacity="1.0">-5</text>
                          <line x1="112" y1="260" x2="120" y2="260" /><text x="105" y="266" textAnchor="end" opacity="1.0">0</text>
                          <line x1="112" y1="220" x2="120" y2="220" /><text x="105" y="226" textAnchor="end" opacity="1.0">5</text>
                          <line x1="112" y1="180" x2="120" y2="180" /><text x="105" y="186" textAnchor="end" opacity="1.0">10</text>
                          <line x1="112" y1="140" x2="120" y2="140" /><text x="105" y="146" textAnchor="end" opacity="1.0">15</text>
                          <line x1="112" y1="100" x2="120" y2="100" /><text x="105" y="106" textAnchor="end" opacity="1.0">20</text>
                        </g>

                        {/* BIGGER Professional pitch dots with enhanced hover */}
                        {Object.entries(pitchData).map(([pitchType, d]) => {
                          const colors = {
                            'Four-Seam Fastball': '#3B82F6', 'Slider': '#F59E0B', 'Changeup': '#10B981', 'Sinker': '#8B5CF6', 'Curveball': '#EF4444'
                          };
                          const color = colors[pitchType] || '#9CA3AF';
                          // Properly align with axis coordinates
                          const x = Math.max(120, Math.min(440, 280 + (d.hb * 8))); // HB: -15 to +20 maps to 160-440
                          const y = Math.max(100, Math.min(340, 260 - (d.ivb * 8))); // IVB: -10 to +20 maps to 340-100 (CROPPED)

                          return (
                            <g key={pitchType} className="group cursor-pointer">
                              {/* Larger hover area */}
                              <circle cx={x} cy={y} r="20" fill="transparent" className="cursor-pointer" />
                              
                              {/* Enhanced outer glow - always visible */}
                              <circle 
                                cx={x} 
                                cy={y} 
                                r="25" 
                                fill="none" 
                                stroke={color} 
                                strokeWidth="2" 
                                opacity="0.4"
                                style={{ 
                                  filter: `drop-shadow(0 0 12px ${color}88)`,
                                  pointerEvents: 'none'
                                }}
                              />
                              
                              {/* BIGGER glow ring for hovered state */}
                              <circle 
                                cx={x} 
                                cy={y} 
                                r="35" 
                                fill="none" 
                                stroke={color} 
                                strokeWidth="3" 
                                opacity="0.0"
                                className="group-hover:opacity-60 transition-opacity duration-300"
                                style={{ 
                                  filter: `drop-shadow(0 0 20px ${color}AA)`,
                                  pointerEvents: 'none'
                                }}
                              />
                              
                              {/* BIGGER Main Thunder dot */}
                              <circle 
                                cx={x} 
                                cy={y} 
                                r="12" 
                                fill={color} 
                                stroke="rgba(255,255,255,0.8)" 
                                strokeWidth="3" 
                                opacity="0.95"
                                className="group-hover:opacity-100 group-hover:r-14 transition-all duration-200"
                                style={{ 
                                  filter: `drop-shadow(0 0 10px ${color}99) contrast(1.2)`,
                                  pointerEvents: 'none'
                                }}
                              />
                              
                              {/* Enhanced inner highlights for depth */}
                              <circle cx={x} cy={y} r="5" fill="rgba(255,255,255,0.5)" style={{ pointerEvents: 'none' }} />
                              <circle cx={x} cy={y} r="2.5" fill="rgba(255,255,255,0.8)" style={{ pointerEvents: 'none' }} />
                              <circle cx={x} cy={y} r="1" fill="rgba(255,255,255,0.95)" style={{ pointerEvents: 'none' }} />
                              
                              {/* Professional hover tooltip */}
                              <g className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                                {(() => {
                                  const tooltipWidth = 220;
                                  const tooltipHeight = 75;
                                  let tooltipX, tooltipY;
                                  
                                  // Position horizontally (left or right), never vertically
                                  if (x < 300) {
                                    // Position to the right of the dot
                                    tooltipX = x + 20;
                                    tooltipY = y - tooltipHeight / 2;
                                  } else {
                                    // Position to the left of the dot
                                    tooltipX = x - tooltipWidth - 20;
                                    tooltipY = y - tooltipHeight / 2;
                                  }
                                  
                                  // Keep tooltip within chart bounds and away from axes
                                  if (tooltipX < 130) tooltipX = 130; // Stay away from Y-axis
                                  if (tooltipX + tooltipWidth > 580) tooltipX = 580 - tooltipWidth;
                                  if (tooltipY < 90) tooltipY = 90; // Stay away from top
                                  if (tooltipY + tooltipHeight > 340) tooltipY = 340 - tooltipHeight; // Stay away from X-axis
                                  
                                  const textCenterX = tooltipX + tooltipWidth / 2;
                                  
                                  return (
                                    <>
                                      {/* Yellow tooltip box */}
                                      <rect 
                                        x={tooltipX} 
                                        y={tooltipY} 
                                        width={tooltipWidth} 
                                        height={tooltipHeight} 
                                        fill="#fbbf24" 
                                        stroke="#f59e0b" 
                                        strokeWidth="2"
                                        rx="8"
                                        style={{ 
                                          filter: 'drop-shadow(0 8px 25px rgba(0, 0, 0, 0.4))'
                                        }}
                                      />
                                      {/* Pitch type name */}
                                      <text 
                                        x={textCenterX} 
                                        y={tooltipY + 22} 
                                        textAnchor="middle" 
                                        fill="#1f2937" 
                                        fontSize="16" 
                                        fontWeight="700"
                                      >
                                        {pitchType}
                                      </text>
                                      
                                      {/* Velocity and movement data */}
                                      <text 
                                        x={textCenterX} 
                                        y={tooltipY + 44} 
                                        textAnchor="middle" 
                                        fill="#1f2937" 
                                        fontSize="15" 
                                        fontWeight="600"
                                      >
                                        {d.velo} {d.ivb?.toFixed(1)}" IVB • {d.hb?.toFixed(1)}" HB
                                      </text>
                                      
                                      {/* Extension info */}
                                      <text 
                                        x={textCenterX} 
                                        y={tooltipY + 62} 
                                        textAnchor="middle" 
                                        fill="#374151" 
                                        fontSize="14" 
                                        fontWeight="500"
                                      >
                                        Extension: {d.extension?.toFixed(1) || '6.2'}"
                                      </text>
                                    </>
                                  );
                                })()}
                              </g>
                            </g>
                          );
                        })}
                      </svg>
                    </div>
                  </CardContent>
                </Card>

                {/* PLATOON EFFECTIVENESS - LEGEND AT TOP */}
                <Card className="min-w-0">
                  <CardContent className="p-4">
                    <div className="mb-3">
                      <h3 className="text-xl font-bold text-white mb-1">Platoon Splits</h3>
                      <p className="text-sm text-white/70">Whiff Rate % vs LHB / RHB</p>
                    </div>
                    
                    {/* LEGEND AT TOP */}
                    <div className="mb-4 p-3 bg-slate-700/20 rounded-lg">
                      <div className="flex justify-center gap-6 mb-2">
                        <div className="flex items-center gap-2"><div className="w-4 h-4 bg-blue-500 rounded" /><span className="text-sm text-white">vs Left-Handed Batters</span></div>
                        <div className="flex items-center gap-2"><div className="w-4 h-4 bg-orange-500 rounded" /><span className="text-sm text-white">vs Right-Handed Batters</span></div>
                      </div>
                      <div className="text-xs text-white/70 text-center">Usage rates: {platoonEffectivenessData.map(d => `${d.pitch} ${d.usage}%`).join(' • ')}</div>
                    </div>

                    <div className="space-y-4">
                      {platoonEffectivenessData.map((d) => {
                        const colors = {
                          'FB': '#3B82F6', 'SL': '#F59E0B', 'CH': '#10B981', 'SI': '#8B5CF6', 'CB': '#EF4444'
                        };
                        const color = colors[d.pitch] || '#9CA3AF';
                        
                        // Fixed whiff rates based on pitcher quality and pitch type
                        const pitcherQuality = selectedPitcher === 'Carlos Alvarez' ? 'elite' : 
                                             selectedPitcher === 'Miguel Acosta' ? 'above-average' :
                                             selectedPitcher === 'Jake Allen' ? 'average' : 'below-average';
                        
                        // Fixed values that won't change on re-render
                        const whiffRates = {
                          'elite': {
                            'FB': { lhb: 90, rhb: 83 },
                            'SL': { lhb: 84, rhb: 81 },
                            'CH': { lhb: 88, rhb: 82 },
                            'SI': { lhb: 77, rhb: 70 },
                            'CB': { lhb: 79, rhb: 84 }
                          },
                          'above-average': {
                            'FB': { lhb: 75, rhb: 68 },
                            'SL': { lhb: 72, rhb: 69 },
                            'CH': { lhb: 74, rhb: 67 },
                            'SI': { lhb: 65, rhb: 58 },
                            'CB': { lhb: 67, rhb: 72 }
                          },
                          'average': {
                            'FB': { lhb: 58, rhb: 52 },
                            'SL': { lhb: 55, rhb: 53 },
                            'CH': { lhb: 57, rhb: 51 },
                            'SI': { lhb: 48, rhb: 43 },
                            'CB': { lhb: 50, rhb: 55 }
                          },
                          'below-average': {
                            'FB': { lhb: 35, rhb: 30 },
                            'SL': { lhb: 32, rhb: 34 },
                            'CH': { lhb: 34, rhb: 29 },
                            'SI': { lhb: 28, rhb: 25 },
                            'CB': { lhb: 30, rhb: 33 }
                          }
                        };
                        
                        const rates = whiffRates[pitcherQuality][d.pitch] || { lhb: 50, rhb: 50 };
                        const lhbWhiff = rates.lhb;
                        const rhbWhiff = rates.rhb;
                        
                        return (
                          <div key={d.pitch} className="space-y-2">
                            <div className="text-white text-sm font-medium">{d.pitch}</div>
                            <div className="grid grid-cols-2 gap-3">
                              <div className="space-y-1">
                                <div className="flex justify-between text-sm">
                                  <span className="text-blue-400">vs LHB</span>
                                  <span className="text-white">{lhbWhiff}%</span>
                                </div>
                                <div className="bg-slate-700 rounded-full h-3 overflow-hidden">
                                  <div 
                                    className="h-full bg-blue-500 rounded-full transition-all duration-500"
                                    style={{ width: `${lhbWhiff}%` }}
                                  />
                                </div>
                              </div>
                              <div className="space-y-1">
                                <div className="flex justify-between text-sm">
                                  <span className="text-orange-400">vs RHB</span>
                                  <span className="text-white">{rhbWhiff}%</span>
                                </div>
                                <div className="bg-slate-700 rounded-full h-3 overflow-hidden">
                                  <div 
                                    className="h-full bg-orange-500 rounded-full transition-all duration-500"
                                    style={{ width: `${rhbWhiff}%` }}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* RIGHT COLUMN - SWING & MISS + PLATOON */}
              <div className="space-y-4">
                {/* SWING & MISS - RESTORED ORIGINAL BIG DOTS */}
                <Card className="min-w-0">
                  <CardContent className="p-4">
                    <div className="mb-3">
                      <h3 className="text-xl font-bold text-white mb-1">Contact Quality</h3>
                      <p className="text-sm text-white/70">Swing-and-miss vs. contact quality by pitch type</p>
                    </div>
                    <div className="h-80 bg-slate-700/30 rounded-lg p-4 relative overflow-visible">
                      {/* Background quadrants with LOWER z-index */}
                      <div className="absolute inset-4 grid grid-cols-2 grid-rows-2 gap-1 pointer-events-none" style={{ zIndex: 1 }}>
                        <div className="bg-green-500/10 border border-green-500/20 rounded-tl flex items-center justify-center">
                          <span className="text-green-400 text-sm font-bold">Elite S&M</span>
                        </div>
                        <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-tr flex items-center justify-center">
                          <span className="text-yellow-400 text-sm font-bold">Hit Hard</span>
                        </div>
                        <div className="bg-blue-500/10 border border-blue-500/20 rounded-bl flex items-center justify-center">
                          <span className="text-blue-400 text-sm font-bold">Weak Contact</span>
                        </div>
                        <div className="bg-red-500/10 border border-red-500/20 rounded-br flex items-center justify-center">
                          <span className="text-red-400 text-sm font-bold">Hit Often</span>
                        </div>
                      </div>
                      
                      {/* DYNAMIC DOTS - USING REAL DATA */}
                      {swingMissData.map((data, i) => {
                        const colors = {
                          'FB': 'bg-blue-500', 'SL': 'bg-yellow-500', 'CH': 'bg-green-500', 'SI': 'bg-purple-500', 'CB': 'bg-red-500'
                        };
                        const color = colors[data.pitch] || 'bg-gray-500';
                        
                        // STABLE POSITIONING - Based on actual whiff rates and hard contact rates
                        let x, y;
                        
                        // Use actual data values to determine position (no random numbers!)
                        const whiffRate = data.whiffRate || 15;
                        const hardContactRate = data.hardContactRate || 35;
                        
                        // Create deterministic offset based on pitch type for visual separation
                        const pitchOffsets = {
                          'FB': { x: 0, y: 0 },
                          'SL': { x: 2, y: 1 },
                          'CH': { x: -1, y: 2 },
                          'SI': { x: 1, y: -1 },
                          'CB': { x: -2, y: 0 }
                        };
                        const offset = pitchOffsets[data.pitch] || { x: 0, y: 0 };
                        
                        // DETERMINISTIC POSITIONING - Based on actual whiff and hard contact rates
                        // X-axis: Whiff Rate (0-50% range, higher whiff = further left)
                        // Y-axis: Hard Contact Rate (0-60% range, higher hard contact = further down)
                        
                        // Map whiff rate to X position (inverted: high whiff = left side)
                        x = Math.max(5, Math.min(95, 90 - (whiffRate * 1.6))) + offset.x;
                        
                        // Map hard contact rate to Y position (high hard contact = bottom)
                        y = Math.max(5, Math.min(95, 10 + (hardContactRate * 1.4))) + offset.y;
                        
                        // Ensure dots stay within bounds
                        x = Math.max(5, Math.min(95, x));
                        y = Math.max(5, Math.min(95, y));
                        
                        console.log(`${selectedPitcher} - ${data.pitch}: Whiff ${whiffRate}%, Hard Contact ${hardContactRate}% → (${x.toFixed(1)}, ${y.toFixed(1)})`);
                        
                        return (
                          <div 
                            key={data.pitch}
                            className={`absolute w-8 h-8 ${color} rounded-full flex items-center justify-center text-white font-bold shadow-lg hover:scale-110 transition-all cursor-pointer`}
                            style={{ 
                              left: `${x}%`, 
                              top: `${y}%`, 
                              zIndex: 1000 + i,
                              boxShadow: '0 0 15px rgba(255,255,255,0.6)'
                            }}
                            onMouseEnter={(e) => {
                              const rect = e.currentTarget.getBoundingClientRect();
                              setTooltip({
                                visible: true,
                                x: rect.left,
                                y: rect.top,
                                pitch: data.pitch,
                                whiffRate: data.whiffRate,
                                hardContactRate: data.hardContactRate
                              });
                            }}
                            onMouseLeave={() => setTooltip({ ...tooltip, visible: false })}
                          >
                            {data.pitch}
                          </div>
                        );
                      })}
                      
                      {/* Professional Tooltip */}
                      <PitchTooltip 
                        pitch={tooltip.pitch}
                        whiffRate={tooltip.whiffRate}
                        hardContactRate={tooltip.hardContactRate}
                        visible={tooltip.visible}
                        x={tooltip.x}
                        y={tooltip.y}
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* COMMAND METRICS - RESTORED ORIGINAL */}
                <Card className="min-w-0">
                  <CardContent className="p-4">
                    <div className="mb-3">
                      <h3 className="text-xl font-bold text-white mb-1">Command Metrics</h3>
                      <p className="text-sm text-white/70">Strike zone control and first-strike percentage by pitch</p>
                    </div>
                    <div className="space-y-3">
                      {commandMetricsData.map((d, i) => {
                        const colors = {
                          'FB': '#3B82F6', 'SL': '#F59E0B', 'CH': '#10B981', 'SI': '#8B5CF6', 'CB': '#EF4444'
                        };
                        const color = colors[d.pitch] || '#9CA3AF';
                        
                        // Map pitch abbreviations to full names for display
                        const pitchNames = {
                          'FB': 'FB', 'SL': 'Slider', 'CH': 'Changeup', 'SI': 'Sinker', 'CB': 'Curveball'
                        };
                        const pitchName = pitchNames[d.pitch] || d.pitch;
                        
                        return (
                          <div key={d.pitch} className="space-y-2">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
                                <span className="text-white font-medium text-sm">{pitchName}</span>
                              </div>
                              <span className="text-white/60 text-sm">{d.strikeRate}% usage</span>
                            </div>
                            <div className="grid grid-cols-3 gap-3">
                              <div>
                                <div className="flex justify-between mb-1">
                                  <span className="text-white/70 text-xs">Zone%</span>
                                  <span className="text-white text-xs font-medium">{d.zoneRate}%</span>
                                </div>
                                <div className="bg-slate-700 rounded-full h-2">
                                  <div className="h-2 rounded-full transition-all duration-500" style={{ backgroundColor: color, width: `${d.zoneRate}%` }} />
                                </div>
                              </div>
                              <div>
                                <div className="flex justify-between mb-1">
                                  <span className="text-white/70 text-xs">Strike%</span>
                                  <span className="text-white text-xs font-medium">{d.strikeRate}%</span>
                                </div>
                                <div className="bg-slate-700 rounded-full h-2">
                                  <div className="h-2 rounded-full transition-all duration-500" style={{ backgroundColor: color, width: `${d.strikeRate}%` }} />
                                </div>
                              </div>
                              <div>
                                <div className="flex justify-between mb-1">
                                  <span className="text-white/70 text-xs">F-Strike%</span>
                                  <span className="text-white text-xs font-medium">{d.firstStrikeRate}%</span>
                                </div>
                                <div className="bg-slate-700 rounded-full h-2">
                                  <div className="h-2 rounded-full transition-all duration-500" style={{ backgroundColor: color, width: `${d.firstStrikeRate}%` }} />
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </>
        ) : (
          <section className="mt-6">
            <Card>
              <CardContent>
                <div className="text-center py-12">
                  <h2 className="text-xl font-semibold mb-2">Select a Pitcher</h2>
                  <p className="text-white/60">Choose a pitcher from the dropdown above to analyze their pitch types and movement profiles.</p>
                </div>
              </CardContent>
            </Card>
          </section>
        )}
      </main>
    </div>
  );
}
